import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Table} from '@/components/commons/table/Table'
import {AlertTriangleIcon, SquareIcon} from '@/components/ui/icon/Icon'
import {TKey, useTranslation} from '@/translations/i18n'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useOutletContext} from 'react-router-dom'
import {PatientExtended, PatientMedicalRecordColumn} from '@/features/patients/types'
import {TableBodyCell, TableBodyCellText, TableBodyRow} from '@/components/ui/table-atoms/TableAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {formatLocaleDate} from '@/dayjs'
import {projectStatusToColor, projectStatusToLabel} from '@/features/projects/utils'
import {useGetMedicalReportsByPatient} from '@/features/medical-reports/queries/useGetMedicalReportsByPatient'
import {medicalReportKeyMapToLabel} from '@/features/medical-reports/utils'
import {Badge} from '@/components/ui/badge/Badge'
import {patientMedicalRecordReportToLabel} from '@/features/medical-records/utils'
import {MedicalRecordHeader} from '@/features/medical-records/components/medical-record-header/MedicalRecordHeader'
import {StyledPatientDetailsEventsWrapper} from './style'
import {MedicalRecordSearchParams} from '@/features/medical-records/types'
import {MoreActionsCell} from '@/components/ui/more-actions-cell/MoreActionsCell'
import {RowMenu} from '@/features/medical-records/components/row-menu/RowMenu'
import {useState} from 'react'
import {MedicalReportDetailsModal} from '@/features/medical-reports/components/medical-report-details-modal/MedicalReportDetailsModal'
import {MedicalReportSmall} from '@/features/medical-reports/types'

const columnNameToLabel = {
    [PatientMedicalRecordColumn.enum.reportDate]: 'medical_records:report_date',
    [PatientMedicalRecordColumn.enum.reportType]: 'medical_records:report_type',
    [PatientMedicalRecordColumn.enum.status]: 'commons:status',
    [PatientMedicalRecordColumn.enum.consultant]: 'commons:operator',
    [PatientMedicalRecordColumn.enum.signedFor]: 'commons:confirmed_by',
    [PatientMedicalRecordColumn.enum.projectCode]: 'commons:pri_n',
    [PatientMedicalRecordColumn.enum.projectStartDate]: 'accesses:pri_start_date',
    [PatientMedicalRecordColumn.enum.projectExpirationDate]: 'accesses:pri_expiration_date',
    [PatientMedicalRecordColumn.enum.projectStatus]: 'accesses:pri_status'
} as const satisfies Record<Exclude<PatientMedicalRecordColumn, 'moreActions'>, TKey>

export const PatientDetailsMedicalRecord = () => {
    const {t} = useTranslation()
    const {patient} = useOutletContext<{patient: PatientExtended}>()

    const [isModalDetailsOpened, setIsModalDetailsOpened] = useState(false)
    const [selectedMedicalReport, setSelectedMedicalReport] = useState<MedicalReportSmall | null>(null)

    const {searchParams, setSearchParams} = useParsedSearchParams(MedicalRecordSearchParams)
    const medicalReportsQuery = useGetMedicalReportsByPatient({
        urlParams: {patientId: patient.id},
        params: {
            startDate: searchParams.startDate,
            endDate: searchParams.endDate,
            projectId: searchParams.projectId,
            createdByUserId: searchParams.createdByUserId,
            medicalRecordReportTypeId: searchParams.medicalRecordReportTypeId,
            page: searchParams.page,
            limit: searchParams.limit
        }
    })

    return (
        <StyledPatientDetailsEventsWrapper>
            <MedicalRecordHeader
                patientId={patient.id}
                hasNeuromuscularDiagnosis={!!patient.neuromuscularDiagnosis}
                hasProject={!!patient.project}
            />
            {medicalReportsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    style={{border: 'none'}}
                    emptyStateComponent={
                        <EmptyState title={t('medical_records:report_not_found')} style={{border: 'none'}} />
                    }
                    pagination={{
                        ...medicalReportsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    isLoading={medicalReportsQuery.isPending}
                    data={medicalReportsQuery.data?.data ?? []}
                    columnPinning={{
                        right: [PatientMedicalRecordColumn.enum.moreActions]
                    }}
                    rowComponent={({tableBodyRowProps, index, dataItem}) => (
                        <TableBodyRow
                            key={index}
                            onClick={() => {
                                if (dataItem) {
                                    setSelectedMedicalReport(dataItem)
                                    setIsModalDetailsOpened(true)
                                }
                            }}
                            {...tableBodyRowProps}
                        />
                    )}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.date, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.reportDate]),
                            id: PatientMedicalRecordColumn.enum.reportDate,
                            size: () => 120
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.medicalRecordReportType?.keyName
                                            ? t(medicalReportKeyMapToLabel[dataItem.medicalRecordReportType.keyName])
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.reportType]),
                            id: PatientMedicalRecordColumn.enum.reportType,
                            size: () => 320
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <Badge
                                        size="sm"
                                        shape="rectangle"
                                        variant={dataItem.status == 'signed' ? 'success' : 'orange'}
                                    >
                                        {t(patientMedicalRecordReportToLabel[dataItem.status])}
                                    </Badge>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.status]),
                            id: PatientMedicalRecordColumn.enum.status,
                            size: () => 120
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.createdByFullName && dataItem.specializationName
                                            ? `${dataItem.createdByFullName} (${dataItem.specializationName})`
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.consultant]),
                            id: PatientMedicalRecordColumn.enum.consultant,
                            size: () => 350,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.signedForFullName ??
                                            (dataItem.status == 'signed' ? dataItem.createdByFullName : null) ??
                                            '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.signedFor]),
                            id: PatientMedicalRecordColumn.enum.signedFor,
                            size: () => 240
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {dataItem.project?.code ?? t('projects:no_project')}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.projectCode]),
                            id: PatientMedicalRecordColumn.enum.projectCode,
                            minSize: () => 80,
                            size: () => 120
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.project?.startDate
                                            ? formatLocaleDate(dataItem.project.startDate, 'll')
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.projectStartDate]),
                            id: PatientMedicalRecordColumn.enum.projectStartDate,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.project?.expirationDate
                                            ? formatLocaleDate(dataItem.project.expirationDate, 'll')
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.projectExpirationDate]),
                            id: PatientMedicalRecordColumn.enum.projectExpirationDate,
                            size: () => 140
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {dataItem.project?.status ? (
                                                <Flexbox align="center" gap={1}>
                                                    <SquareIcon
                                                        className="custom"
                                                        size={8}
                                                        color={projectStatusToColor[dataItem.project.status]}
                                                    />
                                                    {t(projectStatusToLabel[dataItem.project.status])}
                                                </Flexbox>
                                            ) : (
                                                '-'
                                            )}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[PatientMedicalRecordColumn.enum.projectStatus]),
                            id: PatientMedicalRecordColumn.enum.projectStatus,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <RowMenu
                                        patientId={patient.id}
                                        medicalReport={dataItem}
                                        openDetailsModal={() => {
                                            if (dataItem) {
                                                setSelectedMedicalReport(dataItem)
                                                setIsModalDetailsOpened(true)
                                            }
                                        }}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: PatientMedicalRecordColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
            {isModalDetailsOpened && selectedMedicalReport && (
                <MedicalReportDetailsModal
                    medicalReport={selectedMedicalReport}
                    patientId={patient.id}
                    showEditButton={selectedMedicalReport.status == 'draft'}
                    onClose={() => {
                        setIsModalDetailsOpened(false)
                        setSelectedMedicalReport(null)
                    }}
                />
            )}
        </StyledPatientDetailsEventsWrapper>
    )
}
